/**
 * 兑换码管理页面
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { formatInDisplayTz } from '@/lib/time';
import {
  PlusIcon,
  TrashIcon,
  ArrowLeftIcon,
  TicketIcon,
  DocumentTextIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { codeApi, quizApi } from '@/lib/api';
import { RedeemCode } from '@/types';

type ScopeOption = 'quiz' | 'global' | 'all';

type StatusOption = 'all' | 'available' | 'assigned' | 'revoked';

const STATUS_OPTIONS: { value: StatusOption; label: string }[] = [
  { value: 'all', label: '所有状态' },
  { value: 'available', label: '可用' },
  { value: 'assigned', label: '已分配' },
  { value: 'revoked', label: '已作废' },
];

export default function CodeManagement() {
  const { quizId } = useParams<{ quizId?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showImportModal, setShowImportModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showBindingModal, setShowBindingModal] = useState(false);
  const [importText, setImportText] = useState('');
  const [generateCount, setGenerateCount] = useState(10);
  const [generatePrefix, setGeneratePrefix] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusOption>('all');
  const [scopeFilter, setScopeFilter] = useState<ScopeOption>(quizId ? 'quiz' : 'global');
  const [currentPage, setCurrentPage] = useState(1);
  const [modalQuizId, setModalQuizId] = useState<string>(quizId ?? '');
  const [bindingTarget, setBindingTarget] = useState<RedeemCode | null>(null);
  const [bindingQuizId, setBindingQuizId] = useState<string>('');

  const pageSize = 20;

  useEffect(() => {
    setScopeFilter(quizId ? 'quiz' : 'global');
    setCurrentPage(1);
  }, [quizId]);

  useEffect(() => {
    setModalQuizId(quizId ?? '');
  }, [quizId]);

  const { data: quiz } = useQuery({
    queryKey: ['quiz', quizId],
    queryFn: () => quizApi.getQuiz(quizId!),
    enabled: !!quizId,
  });

  const { data: quizList } = useQuery({
    queryKey: ['admin-quizzes'],
    queryFn: quizApi.getQuizzes,
  });

  const quizzes = quizList?.quizzes ?? [];
  const quizMap = useMemo(() => new Map(quizzes.map((item) => [item.id, item.title])), [quizzes]);

  const effectiveScope: ScopeOption = quizId ? 'quiz' : scopeFilter;

  const { data: codesData, isLoading } = useQuery({
    queryKey: ['codes', quizId ?? 'default', effectiveScope, statusFilter, currentPage],
    queryFn: () =>
      codeApi.getCodes({
        quiz_id: quizId,
        scope: effectiveScope,
        status: statusFilter === 'all' ? undefined : statusFilter,
        page: currentPage,
        limit: pageSize,
      }),
  });

  const codes = codesData?.items ?? [];
  const pagination = codesData?.pagination;

  const importMutation = useMutation({
    mutationFn: (payload: { quiz_id?: string | null; codes: string[] }) => codeApi.importCodes(payload),
    onSuccess: (data, variables) => {
      const targetQuizId = variables.quiz_id;
      const isSpecificQuiz = targetQuizId && targetQuizId !== '';

      toast.success(`成功导入 ${data.imported} 个兑换码`);
      setShowImportModal(false);
      setImportText('');

      // 如果导入的是特定题集的兑换码，且当前不在该题集页面，自动跳转
      if (isSpecificQuiz && quizId !== targetQuizId) {
        toast.success(`正在跳转到题集 "${quizMap.get(targetQuizId) ?? targetQuizId}" 查看新导入的兑换码`, {
          duration: 3000,
        });
        navigate(`/codes/${targetQuizId}`);
      } else {
        // 刷新当前页面数据
        queryClient.invalidateQueries({ queryKey: ['codes'] });
      }
    },
  });

  const generateMutation = useMutation({
    mutationFn: (payload: { quiz_id?: string | null; count: number; prefix?: string }) =>
      codeApi.generateCodes(payload),
    onSuccess: (data, variables) => {
      const targetQuizId = variables.quiz_id;
      const isSpecificQuiz = targetQuizId && targetQuizId !== '';

      toast.success(`成功生成 ${data.codes.length} 个兑换码`);
      setShowGenerateModal(false);
      setGenerateCount(10);
      setGeneratePrefix('');

      // 如果生成的是特定题集的兑换码，且当前不在该题集页面，自动跳转
      if (isSpecificQuiz && quizId !== targetQuizId) {
        toast.success(`正在跳转到题集 "${quizMap.get(targetQuizId) ?? targetQuizId}" 查看新生成的兑换码`, {
          duration: 3000,
        });
        navigate(`/codes/${targetQuizId}`);
      } else {
        // 刷新当前页面数据
        queryClient.invalidateQueries({ queryKey: ['codes'] });
      }
    },
    onError: (error: any) => {
      console.error('Code generation error:', error);

      // Extract detailed error message from response
      let errorMessage = '生成兑换码失败';

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // Show additional details if available
      const errorDetails = error?.response?.data?.details;
      const timestamp = error?.response?.data?.timestamp;

      if (errorDetails) {
        console.error('Error details:', errorDetails);
        errorMessage += `\n详细信息: ${errorDetails}`;
      }

      if (timestamp) {
        console.error('Error timestamp:', timestamp);
      }

      toast.error(errorMessage, {
        duration: 8000, // Show longer for detailed errors
      });
    },
  });

  const updateCodeMutation = useMutation({
    mutationFn: (params: { codeId: string; payload: { status?: 'available' | 'revoked'; quiz_id?: string | null } }) =>
      codeApi.updateCode(params.codeId, params.payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['codes'] });
    },
  });

  const deleteCodeMutation = useMutation({
    mutationFn: (codeId: string) => codeApi.deleteCode(codeId),
    onSuccess: (data) => {
      toast.success(`已删除 ${data.deleted} 个兑换码`);
      queryClient.invalidateQueries({ queryKey: ['codes'] });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (params: { quiz_id?: string; scope?: 'global'; status?: 'available' | 'revoked' }) =>
      codeApi.deleteCodes(params),
    onSuccess: (data) => {
      toast.success(`已清理 ${data.deleted} 个兑换码`);
      queryClient.invalidateQueries({ queryKey: ['codes'] });
    },
  });

  const scopeOptions = useMemo(() => {
    const options: { value: ScopeOption; label: string }[] = [];
    if (!quizId) {
      options.push({ value: 'global', label: '默认题集（共享）' });
      options.push({ value: 'all', label: '全部兑换码' });
    } else {
      options.push({ value: 'quiz', label: '当前题集' });
    }
    return options;
  }, [quizId]);

  const quizSelectOptions = useMemo(
    () => [
      { value: '', label: '默认题集（共享）' },
      ...quizzes.map((item) => ({ value: item.id, label: item.title })),
    ],
    [quizzes],
  );

  const resetPagination = () => {
    setCurrentPage(1);
    queryClient.invalidateQueries({ queryKey: ['codes'] });
  };

  const handleImportSubmit = () => {
    const codesToImport = importText
      .split('\n')
      .map((code) => code.trim())
      .filter((code) => code.length > 0);

    if (codesToImport.length === 0) {
      toast.error('请输入兑换码');
      return;
    }

    importMutation.mutate({
      quiz_id: modalQuizId ? modalQuizId : null,
      codes: codesToImport,
    });
  };

  const handleGenerateSubmit = () => {
    if (!Number.isFinite(generateCount) || generateCount <= 0) {
      toast.error('请输入有效的生成数量');
      return;
    }

    generateMutation.mutate({
      quiz_id: modalQuizId ? modalQuizId : null,
      count: generateCount,
      prefix: generatePrefix || undefined,
    });
  };

  const handleStatusToggle = async (code: RedeemCode, nextStatus: 'available' | 'revoked') => {
    try {
      await updateCodeMutation.mutateAsync({ codeId: code.id, payload: { status: nextStatus } });
      toast.success(nextStatus === 'available' ? '已恢复为可用' : '已作废兑换码');
    } catch (error) {
      console.error(error);
      toast.error('更新状态失败');
    }
  };

  const handleDeleteCode = (code: RedeemCode) => {
    if (code.status === 'assigned') {
      toast.error('已分配的兑换码不能删除');
      return;
    }
    if (!window.confirm('确定要删除这个兑换码吗？')) {
      return;
    }
    deleteCodeMutation.mutate(code.id);
  };

  const openBindingModal = (code: RedeemCode) => {
    setBindingTarget(code);
    setBindingQuizId(code.quiz_id ?? '');
    setShowBindingModal(true);
  };

  const handleBindingSubmit = () => {
    if (!bindingTarget) {
      return;
    }
    updateCodeMutation.mutate(
      {
        codeId: bindingTarget.id,
        payload: { quiz_id: bindingQuizId ? bindingQuizId : null },
      },
      {
        onSuccess: () => {
          toast.success('题集绑定已更新');
          setShowBindingModal(false);
          setBindingTarget(null);
        },
        onError: () => {
          toast.error('更新题集绑定失败');
        },
      },
    );
  };

  const handleBulkDelete = (target: 'all' | 'revoked') => {
    const scopeParam = quizId ? { quiz_id: quizId } : { scope: 'global' as const };
    const statusParam = target === 'revoked' ? { status: 'revoked' as const } : {};
    const confirmMessage = target === 'revoked' ? '确认清理所有已作废的兑换码？' : '确认删除所有匹配条件的兑换码？';
    if (!window.confirm(confirmMessage)) {
      return;
    }
    bulkDeleteMutation.mutate({ ...scopeParam, ...statusParam });
  };

  const renderQuizLabel = (code: RedeemCode) => {
    if (code.quiz_id) {
      return quizMap.get(code.quiz_id) ?? code.quiz_id;
    }
    return '默认题集（共享）';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        {quizId && (
          <button
            onClick={() => navigate('/quizzes')}
            className="btn btn-secondary flex items-center gap-2"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            返回
          </button>
        )}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">兑换码管理</h1>
          {quizId ? (
            <p className="text-gray-600">管理 “{quizMap.get(quizId) ?? quizId}” 的兑换码</p>
          ) : (
            <p className="text-gray-600">管理默认题集与共享兑换码池</p>
          )}
        </div>
      </div>

      {/* Actions and Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => {
                setModalQuizId(quizId ?? '');
                setShowImportModal(true);
              }}
              className="btn btn-primary flex items-center gap-2"
            >
              <DocumentTextIcon className="h-4 w-4" />
              导入兑换码
            </button>
            <button
              onClick={() => {
                setModalQuizId(quizId ?? '');
                setShowGenerateModal(true);
              }}
              className="btn btn-secondary flex items-center gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              生成兑换码
            </button>
            <button
              onClick={() => handleBulkDelete('revoked')}
              className="btn btn-outline flex items-center gap-2"
              disabled={bulkDeleteMutation.isPending}
            >
              <TrashIcon className="h-4 w-4" />
              清理已作废
            </button>
            <button
              onClick={() => handleBulkDelete('all')}
              className="btn btn-outline flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50"
              disabled={bulkDeleteMutation.isPending}
            >
              <TrashIcon className="h-4 w-4" />
              删除全部
            </button>
          </div>

          <div className="flex flex-wrap gap-3">
            {scopeOptions.length > 1 && (
              <select
                value={scopeFilter}
                onChange={(e) => {
                  const value = e.target.value as ScopeOption;
                  setScopeFilter(value);
                  resetPagination();
                }}
                className="form-select"
              >
                {scopeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as StatusOption);
                resetPagination();
              }}
              className="form-select"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Codes List */}
      <div className="bg-white rounded-lg shadow">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
          </div>
        ) : codes.length === 0 ? (
          <div className="py-12 text-center">
            <TicketIcon className="mx-auto mb-4 h-12 w-12 text-gray-300" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">还没有兑换码</h3>
            <p className="mb-4 text-gray-600">先导入或生成一批兑换码吧</p>
            <button
              onClick={() => {
                setModalQuizId(quizId ?? '');
                setShowImportModal(true);
              }}
              className="btn btn-primary"
            >
              导入兑换码
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      兑换码
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      归属题集
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      状态
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      创建时间
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      分配时间
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {codes.map((code) => (
                    <tr key={code.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center">
                          <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">
                            {code.code}
                          </code>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(code.code);
                              toast.success('已复制到剪贴板');
                            }}
                            className="ml-2 text-gray-400 hover:text-gray-600"
                            title="复制兑换码"
                          >
                            <ClipboardDocumentIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                        {renderQuizLabel(code)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                            code.status === 'available'
                              ? 'bg-green-100 text-green-800'
                              : code.status === 'assigned'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {code.status === 'available'
                            ? '可用'
                            : code.status === 'assigned'
                            ? '已分配'
                            : '已作废'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                        {formatInDisplayTz(code.created_at, 'yyyy-MM-dd HH:mm')}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                        {code.assigned_at ? formatInDisplayTz(code.assigned_at, 'yyyy-MM-dd HH:mm') : '-'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm">
                        <div className="flex flex-wrap gap-3">
                          <button
                            onClick={() => openBindingModal(code)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            {code.quiz_id ? '变更题集' : '绑定题集'}
                          </button>
                          {code.status !== 'assigned' && (
                            <button
                              onClick={() => handleStatusToggle(code, code.status === 'available' ? 'revoked' : 'available')}
                              className={
                                code.status === 'available'
                                  ? 'text-red-600 hover:text-red-800'
                                  : 'text-green-600 hover:text-green-800'
                              }
                            >
                              {code.status === 'available' ? '作废' : '恢复'}
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteCode(code)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            删除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination && pagination.totalPages > 1 && (
              <div className="border-t border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    显示 {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, pagination.total)} 条，共 {pagination.total} 条
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage <= 1}
                      className="btn btn-secondary text-sm disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      上一页
                    </button>
                    <span className="px-3 py-1 text-sm">
                      {currentPage} / {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, pagination.totalPages))}
                      disabled={currentPage >= pagination.totalPages}
                      className="btn btn-secondary text-sm disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      下一页
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6">
            <h3 className="mb-4 text-lg font-medium text-gray-900">导入兑换码</h3>
            <div className="space-y-4">
              <div>
                <label className="form-label">关联题集</label>
                <select
                  value={modalQuizId}
                  onChange={(e) => setModalQuizId(e.target.value)}
                  className="form-select"
                >
                  {quizSelectOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">不选择题集时，兑换码将进入默认题集（所有题集共享）。</p>
              </div>
              <div>
                <label className="form-label">兑换码列表（每行一个）</label>
                <textarea
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  className="form-textarea"
                  rows={8}
                  placeholder="CODE1\nCODE2\nCODE3\n..."
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowImportModal(false)} className="btn btn-secondary">
                取消
              </button>
              <button
                onClick={handleImportSubmit}
                className="btn btn-primary"
                disabled={importMutation.isPending}
              >
                {importMutation.isPending ? '导入中...' : '确认导入'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6">
            <h3 className="mb-4 text-lg font-medium text-gray-900">生成兑换码</h3>
            <div className="space-y-4">
              <div>
                <label className="form-label">关联题集</label>
                <select
                  value={modalQuizId}
                  onChange={(e) => setModalQuizId(e.target.value)}
                  className="form-select"
                >
                  {quizSelectOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">不选择题集时，兑换码将进入默认题集（所有题集共享）。</p>
              </div>
              <div>
                <label className="form-label">生成数量</label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={generateCount}
                  onChange={(e) => setGenerateCount(Number.parseInt(e.target.value, 10) || 10)}
                  className="form-input"
                  placeholder="10"
                />
              </div>
              <div>
                <label className="form-label">前缀（可选）</label>
                <input
                  type="text"
                  value={generatePrefix}
                  onChange={(e) => setGeneratePrefix(e.target.value)}
                  className="form-input"
                  placeholder="QUIZ2025"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowGenerateModal(false)} className="btn btn-secondary">
                取消
              </button>
              <button
                onClick={handleGenerateSubmit}
                className="btn btn-primary"
                disabled={generateMutation.isPending}
              >
                {generateMutation.isPending ? '生成中...' : '确认生成'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Binding Modal */}
      {showBindingModal && bindingTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6">
            <h3 className="mb-4 text-lg font-medium text-gray-900">更新题集绑定</h3>
            <p className="mb-4 text-sm text-gray-600">
              当前兑换码：<code className="ml-1 rounded bg-gray-100 px-2 py-1 font-mono text-sm">{bindingTarget.code}</code>
            </p>
            <div className="space-y-4">
              <div>
                <label className="form-label">选择题集</label>
                <select
                  value={bindingQuizId}
                  onChange={(e) => setBindingQuizId(e.target.value)}
                  className="form-select"
                >
                  {quizSelectOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-gray-500">选择“默认题集（共享）”可以解除题集绑定，让兑换码对所有题集生效。</p>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowBindingModal(false);
                  setBindingTarget(null);
                }}
                className="btn btn-secondary"
              >
                取消
              </button>
              <button
                onClick={handleBindingSubmit}
                className="btn btn-primary"
                disabled={updateCodeMutation.isPending}
              >
                {updateCodeMutation.isPending ? '保存中...' : '确认更新'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
